import { useState } from 'react';

export default function JobApplication() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [formData, setFormData] = useState({
    position: '',
    applicationDate: '',
    fullName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    dob: '',
    legallyAuthorized: '',
    reliableTransportation: '',
    availabilityDays: false,
    availabilityNights: false,
    availabilityWeekends: false,
    availabilityLiveIn: false,
    availabilityPRN: false,
    cnaCertification: false,
    nurseAideTraining: false,
    otherCredential: false,
    licenseNumber: '',
    licenseExpiration: '',
    cprCertified: '',
    cprExpiration: '',
    companionExperience: '',
    companionExperienceDetails: '',
    convicted: '',
    convictedDetails: '',
    abuseAccusation: '',
    willingGCHEXS: false,
    willingTB: false,
    willingDrug: false,
    willingOrientation: false,
    employer1Name: '',
    employer1Supervisor: '',
    employer1Phone: '',
    employer1Position: '',
    employer1From: '',
    employer1To: '',
    employer1Reason: '',
    employer2Name: '',
    employer2Supervisor: '',
    employer2Phone: '',
    employer2Position: '',
    employer2From: '',
    employer2To: '',
    employer2Reason: '',
    ref1Name: '',
    ref1Phone: '',
    ref1Relationship: '',
    ref2Name: '',
    ref2Phone: '',
    ref2Relationship: '',
    skillBathing: false,
    skillToileting: false,
    skillTransfers: false,
    skillFeeding: false,
    skillDementia: false,
    skillMealPrep: false,
    skillHousekeeping: false,
    skillCompanionship: false,
    skillDocumentation: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();

      formBody.append('Position Applying For', formData.position);
      formBody.append('Date of Application', formData.applicationDate);
      formBody.append('Full Legal Name', formData.fullName);
      formBody.append('Address', formData.address);
      formBody.append('City', formData.city);
      formBody.append('State', formData.state);
      formBody.append('Zip', formData.zip);
      formBody.append('Phone Number', formData.phone);
      formBody.append('Email Address', formData.email);
      formBody.append('Date of Birth', formData.dob);
      formBody.append('Legally Authorized to Work', formData.legallyAuthorized);
      formBody.append('Reliable Transportation', formData.reliableTransportation);

      const availability = [];
      if (formData.availabilityDays) availability.push('Days');
      if (formData.availabilityNights) availability.push('Nights');
      if (formData.availabilityWeekends) availability.push('Weekends');
      if (formData.availabilityLiveIn) availability.push('Live-in');
      if (formData.availabilityPRN) availability.push('PRN/On-Call');
      formBody.append('Availability', availability.join(', '));

      const credentials = [];
      if (formData.cnaCertification) credentials.push('CNA Certification');
      if (formData.nurseAideTraining) credentials.push('Nurse Aide Training Program');
      if (formData.otherCredential) credentials.push('Other DCH-Approved Credential');
      formBody.append('Credentials', credentials.join(', '));

      formBody.append('License/Certification Number', formData.licenseNumber);
      formBody.append('License Expiration Date', formData.licenseExpiration);
      formBody.append('CPR/First Aid Certified', formData.cprCertified);
      formBody.append('CPR Expiration Date', formData.cprExpiration);
      formBody.append('Companion Experience', formData.companionExperience);
      formBody.append('Companion Experience Details', formData.companionExperienceDetails);
      formBody.append('Convicted', formData.convicted);
      formBody.append('Convicted Details', formData.convictedDetails);
      formBody.append('Abuse Accusation', formData.abuseAccusation);

      const compliance = [];
      if (formData.willingGCHEXS) compliance.push('GCHEXS');
      if (formData.willingTB) compliance.push('TB Screening');
      if (formData.willingDrug) compliance.push('Drug Screening');
      if (formData.willingOrientation) compliance.push('Orientation Training');
      formBody.append('Willing to Complete', compliance.join(', '));

      formBody.append('Employer 1 Name', formData.employer1Name);
      formBody.append('Employer 1 Supervisor', formData.employer1Supervisor);
      formBody.append('Employer 1 Phone', formData.employer1Phone);
      formBody.append('Employer 1 Position', formData.employer1Position);
      formBody.append('Employer 1 From', formData.employer1From);
      formBody.append('Employer 1 To', formData.employer1To);
      formBody.append('Employer 1 Reason', formData.employer1Reason);

      formBody.append('Employer 2 Name', formData.employer2Name);
      formBody.append('Employer 2 Supervisor', formData.employer2Supervisor);
      formBody.append('Employer 2 Phone', formData.employer2Phone);
      formBody.append('Employer 2 Position', formData.employer2Position);
      formBody.append('Employer 2 From', formData.employer2From);
      formBody.append('Employer 2 To', formData.employer2To);
      formBody.append('Employer 2 Reason', formData.employer2Reason);

      formBody.append('Reference 1 Name', formData.ref1Name);
      formBody.append('Reference 1 Phone', formData.ref1Phone);
      formBody.append('Reference 1 Relationship', formData.ref1Relationship);
      formBody.append('Reference 2 Name', formData.ref2Name);
      formBody.append('Reference 2 Phone', formData.ref2Phone);
      formBody.append('Reference 2 Relationship', formData.ref2Relationship);

      const skills = [];
      if (formData.skillBathing) skills.push('Bathing assistance');
      if (formData.skillToileting) skills.push('Toileting & incontinent care');
      if (formData.skillTransfers) skills.push('Safe transfers');
      if (formData.skillFeeding) skills.push('Feeding assistance');
      if (formData.skillDementia) skills.push('Dementia care');
      if (formData.skillMealPrep) skills.push('Meal preparation');
      if (formData.skillHousekeeping) skills.push('Light housekeeping');
      if (formData.skillCompanionship) skills.push('Companionship');
      if (formData.skillDocumentation) skills.push('Documentation/care notes');
      formBody.append('Skills', skills.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d6chjiqohb161tfd8390', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          position: '',
          applicationDate: '',
          fullName: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          email: '',
          dob: '',
          legallyAuthorized: '',
          reliableTransportation: '',
          availabilityDays: false,
          availabilityNights: false,
          availabilityWeekends: false,
          availabilityLiveIn: false,
          availabilityPRN: false,
          cnaCertification: false,
          nurseAideTraining: false,
          otherCredential: false,
          licenseNumber: '',
          licenseExpiration: '',
          cprCertified: '',
          cprExpiration: '',
          companionExperience: '',
          companionExperienceDetails: '',
          convicted: '',
          convictedDetails: '',
          abuseAccusation: '',
          willingGCHEXS: false,
          willingTB: false,
          willingDrug: false,
          willingOrientation: false,
          employer1Name: '',
          employer1Supervisor: '',
          employer1Phone: '',
          employer1Position: '',
          employer1From: '',
          employer1To: '',
          employer1Reason: '',
          employer2Name: '',
          employer2Supervisor: '',
          employer2Phone: '',
          employer2Position: '',
          employer2From: '',
          employer2To: '',
          employer2Reason: '',
          ref1Name: '',
          ref1Phone: '',
          ref1Relationship: '',
          ref2Name: '',
          ref2Phone: '',
          ref2Relationship: '',
          skillBathing: false,
          skillToileting: false,
          skillTransfers: false,
          skillFeeding: false,
          skillDementia: false,
          skillMealPrep: false,
          skillHousekeeping: false,
          skillCompanionship: false,
          skillDocumentation: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-[#6B8E6F] focus:ring-2 focus:ring-[#6B8E6F]/20 outline-none transition-all text-sm bg-white';
  const labelClass = 'block text-sm font-medium text-[#2C2C2C] mb-1.5';

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif text-[#2C2C2C] mb-2">GENTLE ROOTS HOME CARE</h1>
          <h2 className="text-3xl font-serif text-[#6B8E6F] mb-6">Employment Application</h2>
          <div className="text-gray-600 space-y-1 mb-6">
            <p>800 Battery Avenue SE, Suite 300</p>
            <p>Office #3076</p>
            <p>Atlanta, GA 30339</p>
            <p>Phone: 954-716-2020</p>
            <p>Email: info@gentlerootshomecare.com</p>
          </div>
          <div className="bg-[#FAF8F5] rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm text-[#2C2C2C] font-medium mb-2">Gentle Roots Home Care is an Equal Opportunity Employer.</p>
            <p className="text-sm text-[#2C2C2C] font-medium">We hire W-2 employees only.</p>
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-[#6B8E6F] text-[#6B8E6F] rounded-xl font-medium text-sm hover:bg-[#6B8E6F] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line w-5 h-5 flex items-center justify-center"></i>
              Download Application (Print to PDF)
            </button>
          </div>

        </div>

        <form
          id="job-application-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="space-y-10"
        >
          {/* Position Applying For */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              🔹 POSITION APPLYING FOR
            </h3>
            <div className="space-y-3 mb-4">
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="position"
                  value="Personal Care Assistant (PCA)"
                  checked={formData.position === 'Personal Care Assistant (PCA)'}
                  onChange={(e) => setFormData({ ...formData, position: e.target.checked ? e.target.value : '' })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Personal Care Assistant (PCA)
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="position"
                  value="Companion Sitter"
                  checked={formData.position === 'Companion Sitter'}
                  onChange={(e) => setFormData({ ...formData, position: e.target.checked ? e.target.value : '' })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Companion Sitter
              </label>
            </div>
            <div>
              <label className={labelClass}>Date of Application *</label>
              <input
                type="date"
                name="applicationDate"
                required
                value={formData.applicationDate}
                onChange={(e) => setFormData({ ...formData, applicationDate: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          {/* Section 1 - Applicant Information */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 1 – APPLICANT INFORMATION
            </h3>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Full Legal Name *</label>
                <input
                  type="text"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Address *</label>
                <input
                  type="text"
                  name="address"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>City *</label>
                  <input
                    type="text"
                    name="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>State *</label>
                  <input
                    type="text"
                    name="state"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Zip *</label>
                  <input
                    type="text"
                    name="zip"
                    required
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Date of Birth (Must be 18+) *</label>
                <input
                  type="date"
                  name="dob"
                  required
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Are you legally authorized to work in the U.S.? *</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="legallyAuthorized"
                      value="Yes"
                      checked={formData.legallyAuthorized === 'Yes'}
                      onChange={(e) => setFormData({ ...formData, legallyAuthorized: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="legallyAuthorized"
                      value="No"
                      checked={formData.legallyAuthorized === 'No'}
                      onChange={(e) => setFormData({ ...formData, legallyAuthorized: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    No
                  </label>
                </div>
              </div>
              <div>
                <label className={labelClass}>Do you have reliable transportation? *</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="reliableTransportation"
                      value="Yes"
                      checked={formData.reliableTransportation === 'Yes'}
                      onChange={(e) => setFormData({ ...formData, reliableTransportation: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="reliableTransportation"
                      value="No"
                      checked={formData.reliableTransportation === 'No'}
                      onChange={(e) => setFormData({ ...formData, reliableTransportation: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    No
                  </label>
                </div>
              </div>
              <div>
                <label className={labelClass}>Are you available for:</label>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.availabilityDays}
                      onChange={(e) => setFormData({ ...formData, availabilityDays: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    Days
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.availabilityNights}
                      onChange={(e) => setFormData({ ...formData, availabilityNights: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    Nights
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.availabilityWeekends}
                      onChange={(e) => setFormData({ ...formData, availabilityWeekends: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    Weekends
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.availabilityLiveIn}
                      onChange={(e) => setFormData({ ...formData, availabilityLiveIn: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    Live-in
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.availabilityPRN}
                      onChange={(e) => setFormData({ ...formData, availabilityPRN: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    PRN / On-Call
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 - Position Requirements */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 2 – POSITION REQUIREMENTS
            </h3>
            
            <div className="mb-6">
              <p className="text-sm font-semibold text-[#6B8E6F] mb-3">For Personal Care Assistant (PCA):</p>
              <p className="text-sm text-gray-700 mb-3">Do you have one of the following?</p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.cnaCertification}
                    onChange={(e) => setFormData({ ...formData, cnaCertification: e.target.checked })}
                    className="w-4 h-4 accent-[#6B8E6F]"
                  />
                  CNA Certification
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.nurseAideTraining}
                    onChange={(e) => setFormData({ ...formData, nurseAideTraining: e.target.checked })}
                    className="w-4 h-4 accent-[#6B8E6F]"
                  />
                  Completed Nurse Aide Training Program
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.otherCredential}
                    onChange={(e) => setFormData({ ...formData, otherCredential: e.target.checked })}
                    className="w-4 h-4 accent-[#6B8E6F]"
                  />
                  Other DCH-Approved Healthcare Credential
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelClass}>License/Certification Number (if applicable)</label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Expiration Date</label>
                  <input
                    type="date"
                    name="licenseExpiration"
                    value={formData.licenseExpiration}
                    onChange={(e) => setFormData({ ...formData, licenseExpiration: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelClass}>Are you CPR/First Aid Certified?</label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                      <input
                        type="radio"
                        name="cprCertified"
                        value="Yes"
                        checked={formData.cprCertified === 'Yes'}
                        onChange={(e) => setFormData({ ...formData, cprCertified: e.target.value })}
                        className="accent-[#6B8E6F]"
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                      <input
                        type="radio"
                        name="cprCertified"
                        value="No"
                        checked={formData.cprCertified === 'No'}
                        onChange={(e) => setFormData({ ...formData, cprCertified: e.target.value })}
                        className="accent-[#6B8E6F]"
                      />
                      No
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>CPR Expiration Date</label>
                  <input
                    type="date"
                    name="cprExpiration"
                    value={formData.cprExpiration}
                    onChange={(e) => setFormData({ ...formData, cprExpiration: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#6B8E6F] mb-3">For Companion Sitter:</p>
              <div className="mb-3">
                <label className={labelClass}>Do you have experience with elderly, disabled, or special needs clients?</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="companionExperience"
                      value="Yes"
                      checked={formData.companionExperience === 'Yes'}
                      onChange={(e) => setFormData({ ...formData, companionExperience: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="companionExperience"
                      value="No"
                      checked={formData.companionExperience === 'No'}
                      onChange={(e) => setFormData({ ...formData, companionExperience: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    No
                  </label>
                </div>
              </div>
              <div>
                <label className={labelClass}>Explain briefly:</label>
                <textarea
                  name="companionExperienceDetails"
                  maxLength={500}
                  value={formData.companionExperienceDetails}
                  onChange={(e) => setFormData({ ...formData, companionExperienceDetails: e.target.value })}
                  className={inputClass}
                  rows={3}
                  placeholder="Describe your experience..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.companionExperienceDetails.length}/500 characters</p>
              </div>
            </div>
          </div>

          {/* Section 3 - Background & Compliance */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 3 – BACKGROUND & COMPLIANCE
            </h3>
            <p className="text-sm text-gray-600 mb-6 italic">Georgia law requires fingerprint-based background checks through GCHEXS.</p>
            
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Have you ever been convicted of a felony or misdemeanor (excluding minor traffic violations)?</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="convicted"
                      value="Yes"
                      checked={formData.convicted === 'Yes'}
                      onChange={(e) => setFormData({ ...formData, convicted: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="convicted"
                      value="No"
                      checked={formData.convicted === 'No'}
                      onChange={(e) => setFormData({ ...formData, convicted: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    No
                  </label>
                </div>
                {formData.convicted === 'Yes' && (
                  <div className="mt-3">
                    <label className={labelClass}>If yes, explain:</label>
                    <textarea
                      name="convictedDetails"
                      maxLength={500}
                      value={formData.convictedDetails}
                      onChange={(e) => setFormData({ ...formData, convictedDetails: e.target.value })}
                      className={inputClass}
                      rows={3}
                      placeholder="Please explain..."
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.convictedDetails.length}/500 characters</p>
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>Have you ever been accused of abuse, neglect, or exploitation of a vulnerable adult?</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="abuseAccusation"
                      value="Yes"
                      checked={formData.abuseAccusation === 'Yes'}
                      onChange={(e) => setFormData({ ...formData, abuseAccusation: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="abuseAccusation"
                      value="No"
                      checked={formData.abuseAccusation === 'No'}
                      onChange={(e) => setFormData({ ...formData, abuseAccusation: e.target.value })}
                      className="accent-[#6B8E6F]"
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className={labelClass}>Are you willing to complete:</label>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.willingGCHEXS}
                      onChange={(e) => setFormData({ ...formData, willingGCHEXS: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    GCHEXS fingerprint background check
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.willingTB}
                      onChange={(e) => setFormData({ ...formData, willingTB: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    TB screening
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.willingDrug}
                      onChange={(e) => setFormData({ ...formData, willingDrug: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    Drug screening (if required)
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.willingOrientation}
                      onChange={(e) => setFormData({ ...formData, willingOrientation: e.target.checked })}
                      className="w-4 h-4 accent-[#6B8E6F]"
                    />
                    Orientation training
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4 - Work History */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 4 – WORK HISTORY
            </h3>

            <div className="mb-8">
              <p className="text-sm font-semibold text-[#6B8E6F] mb-4">Most Recent Employer:</p>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    name="employer1Name"
                    value={formData.employer1Name}
                    onChange={(e) => setFormData({ ...formData, employer1Name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Supervisor Name</label>
                    <input
                      type="text"
                      name="employer1Supervisor"
                      value={formData.employer1Supervisor}
                      onChange={(e) => setFormData({ ...formData, employer1Supervisor: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input
                      type="tel"
                      name="employer1Phone"
                      value={formData.employer1Phone}
                      onChange={(e) => setFormData({ ...formData, employer1Phone: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Position Held</label>
                  <input
                    type="text"
                    name="employer1Position"
                    value={formData.employer1Position}
                    onChange={(e) => setFormData({ ...formData, employer1Position: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Dates Employed From</label>
                    <input
                      type="text"
                      name="employer1From"
                      value={formData.employer1From}
                      onChange={(e) => setFormData({ ...formData, employer1From: e.target.value })}
                      className={inputClass}
                      placeholder="MM/YYYY"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>To</label>
                    <input
                      type="text"
                      name="employer1To"
                      value={formData.employer1To}
                      onChange={(e) => setFormData({ ...formData, employer1To: e.target.value })}
                      className={inputClass}
                      placeholder="MM/YYYY"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Reason for Leaving</label>
                  <input
                    type="text"
                    name="employer1Reason"
                    value={formData.employer1Reason}
                    onChange={(e) => setFormData({ ...formData, employer1Reason: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#6B8E6F] mb-4">Previous Employer:</p>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    name="employer2Name"
                    value={formData.employer2Name}
                    onChange={(e) => setFormData({ ...formData, employer2Name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Supervisor Name</label>
                    <input
                      type="text"
                      name="employer2Supervisor"
                      value={formData.employer2Supervisor}
                      onChange={(e) => setFormData({ ...formData, employer2Supervisor: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input
                      type="tel"
                      name="employer2Phone"
                      value={formData.employer2Phone}
                      onChange={(e) => setFormData({ ...formData, employer2Phone: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Position Held</label>
                  <input
                    type="text"
                    name="employer2Position"
                    value={formData.employer2Position}
                    onChange={(e) => setFormData({ ...formData, employer2Position: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Dates Employed From</label>
                    <input
                      type="text"
                      name="employer2From"
                      value={formData.employer2From}
                      onChange={(e) => setFormData({ ...formData, employer2From: e.target.value })}
                      className={inputClass}
                      placeholder="MM/YYYY"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>To</label>
                    <input
                      type="text"
                      name="employer2To"
                      value={formData.employer2To}
                      onChange={(e) => setFormData({ ...formData, employer2To: e.target.value })}
                      className={inputClass}
                      placeholder="MM/YYYY"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Reason for Leaving</label>
                  <input
                    type="text"
                    name="employer2Reason"
                    value={formData.employer2Reason}
                    onChange={(e) => setFormData({ ...formData, employer2Reason: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 - Professional References */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 5 – PROFESSIONAL REFERENCES
            </h3>

            <div className="mb-6">
              <p className="text-sm font-semibold text-[#6B8E6F] mb-4">Reference #1</p>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Name</label>
                  <input
                    type="text"
                    name="ref1Name"
                    value={formData.ref1Name}
                    onChange={(e) => setFormData({ ...formData, ref1Name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input
                      type="tel"
                      name="ref1Phone"
                      value={formData.ref1Phone}
                      onChange={(e) => setFormData({ ...formData, ref1Phone: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Relationship</label>
                    <input
                      type="text"
                      name="ref1Relationship"
                      value={formData.ref1Relationship}
                      onChange={(e) => setFormData({ ...formData, ref1Relationship: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#6B8E6F] mb-4">Reference #2</p>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Name</label>
                  <input
                    type="text"
                    name="ref2Name"
                    value={formData.ref2Name}
                    onChange={(e) => setFormData({ ...formData, ref2Name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input
                      type="tel"
                      name="ref2Phone"
                      value={formData.ref2Phone}
                      onChange={(e) => setFormData({ ...formData, ref2Phone: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Relationship</label>
                    <input
                      type="text"
                      name="ref2Relationship"
                      value={formData.ref2Relationship}
                      onChange={(e) => setFormData({ ...formData, ref2Relationship: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Skills Checklist */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 6 – SKILLS CHECKLIST
            </h3>
            <p className="text-sm text-gray-700 mb-4">Check all that apply:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillBathing}
                  onChange={(e) => setFormData({ ...formData, skillBathing: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Bathing assistance
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillToileting}
                  onChange={(e) => setFormData({ ...formData, skillToileting: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Toileting & incontinent care
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillTransfers}
                  onChange={(e) => setFormData({ ...formData, skillTransfers: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Safe transfers
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillFeeding}
                  onChange={(e) => setFormData({ ...formData, skillFeeding: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Feeding assistance
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillDementia}
                  onChange={(e) => setFormData({ ...formData, skillDementia: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Dementia care
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillMealPrep}
                  onChange={(e) => setFormData({ ...formData, skillMealPrep: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Meal preparation
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillHousekeeping}
                  onChange={(e) => setFormData({ ...formData, skillHousekeeping: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Light housekeeping
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillCompanionship}
                  onChange={(e) => setFormData({ ...formData, skillCompanionship: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Companionship
              </label>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.skillDocumentation}
                  onChange={(e) => setFormData({ ...formData, skillDocumentation: e.target.checked })}
                  className="w-4 h-4 accent-[#6B8E6F]"
                />
                Documentation / care notes
              </label>
            </div>
          </div>

          {/* Section 7 - Acknowledgments */}
          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-6 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              SECTION 7 – ACKNOWLEDGMENTS
            </h3>
            <p className="text-sm text-gray-700 mb-4 font-medium">I understand that:</p>
            <ul className="space-y-2 text-sm text-gray-700 mb-6">
              <li className="flex gap-2">
                <span>•</span>
                <span>Gentle Roots Home Care provides non-medical services only.</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Employees are hired as W-2 employees (not independent contractors).</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Employment is contingent upon satisfactory GCHEXS background results.</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Providing false information may result in denial or termination.</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>I may be required to complete TB screening and training before hire.</span>
              </li>
            </ul>
            <p className="text-xs text-gray-500 italic">By submitting this application, you acknowledge that you have read and agree to the above statements.</p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-[#6B8E6F] text-white rounded-xl font-medium text-lg hover:bg-[#5a7a5e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
          </button>

          {submitStatus === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm text-center">
              Thank you! Your application has been submitted successfully. We&apos;ll be in touch soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm text-center">
              Something went wrong. Please try again.
            </div>
          )}

          {/* Next Steps - Required Documents */}
          <div className="mt-12 bg-[#FAF8F5] rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-4 pb-3 border-b-2 border-[#6B8E6F] inline-block">
              <i className="ri-mail-send-line w-5 h-5 inline-flex items-center justify-center mr-2"></i>
              Next Steps
            </h3>
            <p className="text-sm text-gray-700 mb-5 leading-relaxed">
              Once you have completed the application, please email the following required documents to{' '}
              <a href="mailto:info@gentlerootshomecare.com" className="text-[#6B8E6F] font-semibold underline cursor-pointer" rel="nofollow">
                info@gentlerootshomecare.com
              </a>:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 flex items-center justify-center bg-[#6B8E6F] text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                <span>Government-issued ID</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 flex items-center justify-center bg-[#6B8E6F] text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                <span>TB Screening (Negative result)</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 flex items-center justify-center bg-[#6B8E6F] text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                <span>CNA license or approved credential (if applying as a PCA)</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 flex items-center justify-center bg-[#6B8E6F] text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                <span>CPR/First Aid certification</span>
              </li>
            </ul>
            <div className="bg-white rounded-xl p-5 border border-[#6B8E6F]/20">
              <p className="text-sm text-gray-700 leading-relaxed">
                After I receive your application and supporting documents, I will review everything and, if approved, send over the remaining onboarding documents along with your offer letter.
              </p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
